import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Divider,
  CircularProgress, Alert, MenuItem, TextField, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar,
  IconButton, Card, CardMedia, CardActions, LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import PetsIcon from '@mui/icons-material/Pets';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { jsPDF } from 'jspdf';
import { useNavigate, useParams } from 'react-router-dom';
import { normalizeBranch, loadLogos, renderStandardHeader, renderStandardFooter } from '../services/pdfCommon';
import { doc, getDoc, updateDoc, serverTimestamp, addDoc, collection, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';

import { COLORS } from '../theme/constants';
import { IMAGING_STATUS_CONFIG, PRIORITY_CONFIG, MODALITY_CONFIG, calcAge } from '../models/imagingModel';

const { primary: IMG_PRIMARY, teal: IMG_TEAL } = COLORS;

const DEFAULT_CLINIC_NAME = 'Hospital Clínico Veterinario UDLA';
const C = { primary: [26, 58, 92], teal: [44, 184, 160], text: [30, 30, 30], muted: [100, 110, 120], light: [244, 246, 248], border: [220, 225, 230], white: [255, 255, 255] };

export const printOrderPDF = async (order, branchData = {}) => {
  const branch = normalizeBranch(branchData);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = 210;
  const margin = 18;
  const cw = pw - margin * 2;

  const logos = await loadLogos();
  const dateStr = order.createdAt?.toDate
    ? order.createdAt.toDate().toLocaleDateString('es-CL')
    : new Date().toLocaleDateString('es-CL');
  renderStandardHeader(doc, branch, logos, {
    title: 'ORDEN DE IMAGENOLOGÍA',
    number: order.orderNumber || '—',
    date: dateStr,
  });

  let y = 40;
  const sf = (arr) => doc.setFillColor(...arr);
  const sc = (arr) => doc.setTextColor(...arr);
  const sd = (arr) => doc.setDrawColor(...arr);

  // ── PATIENT / VET BLOCK ──
  sf(C.light); sd(C.border);
  doc.roundedRect(margin, y, cw, 46, 2, 2, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); sc(C.primary);
  doc.text('DATOS DEL PACIENTE', margin + 4, y + 6);
  sd(C.teal); doc.setLineWidth(0.4);
  doc.line(margin + 4, y + 8, margin + cw / 2 - 4, y + 8);

  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); sc(C.text);
  const col1 = margin + 4;
  const col2 = margin + cw / 2 + 4;
  const lh = 6;
  let ly = y + 14;
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Nombre:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientName || '—', col1 + 20, ly);
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Especie:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientSpecies || '—', col2 + 20, ly);
  ly += lh;
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Raza:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientBreed || '—', col1 + 20, ly);
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Sexo:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.patientSex || '—', col2 + 20, ly);
  ly += lh;
  const age = calcAge(order.patientBirthdate) || order.patientAge || '—';
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Edad:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(age, col1 + 20, ly);
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Propietario:', col2, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.ownerName || '—', col2 + 28, ly);
  ly += lh;
  doc.setFont('helvetica', 'bold'); sc(C.muted); doc.text('Médico V.:', col1, ly); doc.setFont('helvetica', 'normal'); sc(C.text); doc.text(order.vetName || '—', col1 + 24, ly);

  y += 52;

  // ── STUDIES TABLE ──
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9); sc(C.primary);
  doc.text('ESTUDIOS SOLICITADOS', margin, y);
  sd(C.teal); doc.setLineWidth(0.5);
  doc.line(margin, y + 2, margin + 60, y + 2);
  y += 7;

  sf(C.primary); sd(C.primary);
  doc.rect(margin, y, cw, 7, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); sc(C.white);
  doc.text('#', margin + 3, y + 4.8);
  doc.text('Estudio', margin + 12, y + 4.8);
  doc.text('Modalidad', margin + cw - 40, y + 4.8, { align: 'right' });
  y += 7;

  const studies = order.studiesRequested || [];
  studies.forEach((s, i) => {
    sf(i % 2 === 0 ? C.white : C.light); sd(C.border);
    doc.rect(margin, y, cw, 7, 'FD');
    doc.setFont('helvetica', 'normal'); sc(C.muted);
    doc.text(String(i + 1), margin + 3, y + 4.8);
    sc(C.text); doc.setFont('helvetica', 'bold');
    doc.text(s.examTypeName || '—', margin + 12, y + 4.8);
    doc.setFont('helvetica', 'normal'); sc(C.muted);
    doc.text(s.modality || '—', margin + cw - 40, y + 4.8, { align: 'right' });
    y += 7;
  });

  y += 8;

  // ── NOTES ──
  if (order.clinicalIndication) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); sc(C.primary);
    doc.text('INDICACIÓN CLÍNICA', margin, y);
    sd(C.teal); doc.setLineWidth(0.5); doc.line(margin, y + 2, margin + 50, y + 2);
    y += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); sc(C.text);
    const lines = doc.splitTextToSize(order.clinicalIndication, cw);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 6;
  }

  if (order.relevantHistory) {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); sc(C.primary);
    doc.text('HISTORIA RELEVANTE', margin, y);
    sd(C.teal); doc.setLineWidth(0.5); doc.line(margin, y + 2, margin + 50, y + 2);
    y += 8;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); sc(C.text);
    const lines = doc.splitTextToSize(order.relevantHistory, cw);
    doc.text(lines, margin, y);
    y += lines.length * 5 + 6;
  }

  // ── SIGNATURE BLOCK ──
  if (y > 230) { doc.addPage(); y = margin; }
  y = Math.max(y, 210);
  sd(C.border); doc.setLineWidth(0.3);
  doc.line(margin, y, margin + 60, y);
  doc.line(margin + 90, y, margin + 150, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); sc(C.muted);
  doc.text('Firma Médico Veterinario', margin + 2, y + 5);
  doc.text('Firma Propietario / Tutor', margin + 92, y + 5);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(8); sc(C.text);
  const vetNameLines = doc.splitTextToSize(order.vetName || '', 58);
  doc.text(vetNameLines, margin + 2, y + 12);
  const ownerNameLines = doc.splitTextToSize(order.ownerName || '', 58);
  doc.text(ownerNameLines, margin + 92, y + 12);

  renderStandardFooter(doc, branch);

  doc.save(`Orden_${order.orderNumber || order.id}.pdf`);
};

export default function ImagingOrderDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser, userData } = useAuth();
  const { activeBranch } = useBranch();
  const fileInputRef = useRef(null);

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const snap = await getDoc(doc(db, 'imagingOrders', id));
        if (snap.exists()) {
          setOrder({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error('Error loading order:', err);
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
  }, [id]);

  const handleFileUpload = async (files) => {
    if (!order || files.length === 0) return;

    setUploading(true);
    const uploadedFiles = [];

    try {
      for (const file of files) {
        const storageRef = ref(storage, `imagingStudies/${order.id}/${Date.now()}_${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on('state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(Math.round(progress));
            },
            reject,
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              uploadedFiles.push({
                id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                fileName: file.name,
                fileType: file.type,
                storagePath: storageRef.fullPath,
                downloadURL: url,
                fileSizeMB: (file.size / 1048576).toFixed(2),
                uploadedBy: currentUser?.uid || '',
                uploadedAt: new Date(),
                description: '',
                examTypeId: null,
              });
              resolve();
            }
          );
        });
      }

      await updateDoc(doc(db, 'imagingOrders', order.id), {
        files: arrayUnion(...uploadedFiles),
        status: 'imaging_done',
        updatedAt: serverTimestamp(),
      });

      // Audit log
      await addDoc(collection(db, 'imagingOrders', order.id, 'auditLog'), {
        action: 'images_uploaded',
        details: `${files.length} archivo(s) subido(s)`,
        performedBy: currentUser?.uid,
        performedByName: userData?.name || userData?.email,
        timestamp: serverTimestamp(),
      });

      setSnackbar({ open: true, message: `${files.length} imagen(es) subida(s) correctamente`, severity: 'success' });
    } catch (err) {
      console.error('Upload error:', err);
      setSnackbar({ open: true, message: `Error al subir: ${err.message}`, severity: 'error' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteImage = async (fileId) => {
    if (!order) return;

    try {
      const fileToDelete = order.files?.find(f => f.id === fileId);
      if (fileToDelete) {
        await deleteObject(ref(storage, fileToDelete.storagePath));
      }

      await updateDoc(doc(db, 'imagingOrders', order.id), {
        files: order.files.filter(f => f.id !== fileId),
        updatedAt: serverTimestamp(),
      });

      setSnackbar({ open: true, message: 'Imagen eliminada', severity: 'success' });
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({ open: true, message: `Error al eliminar: ${err.message}`, severity: 'error' });
    }
  };

  const handleStatusChange = async () => {
    if (!order || !newStatus) return;

    try {
      await updateDoc(doc(db, 'imagingOrders', order.id), {
        status: newStatus,
        statusHistory: arrayUnion({
          status: newStatus,
          changedAt: new Date(),
          changedBy: currentUser?.uid,
          changedByName: userData?.name || userData?.email,
          notes: '',
        }),
        updatedAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'imagingOrders', order.id, 'auditLog'), {
        action: 'status_changed',
        details: `Estado cambiado a ${newStatus}`,
        performedBy: currentUser?.uid,
        performedByName: userData?.name || userData?.email,
        timestamp: serverTimestamp(),
      });

      setStatusDialogOpen(false);
      setSnackbar({ open: true, message: 'Estado actualizado', severity: 'success' });
    } catch (err) {
      console.error('Status change error:', err);
      setSnackbar({ open: true, message: `Error: ${err.message}`, severity: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Orden no encontrada</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/imaging/orders')}
            sx={{ color: IMG_PRIMARY, fontWeight: 600 }}>Volver</Button>
          <BrokenImageIcon sx={{ color: IMG_PRIMARY, fontSize: 32 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>{order.orderNumber || '—'}</Typography>
              <Chip label={PRIORITY_CONFIG[order.priority]?.label || order.priority} size="small"
                sx={{ bgcolor: PRIORITY_CONFIG[order.priority]?.bg, color: PRIORITY_CONFIG[order.priority]?.color, fontWeight: 600 }} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {order.requestedAt?.toDate ? order.requestedAt.toDate().toLocaleString('es-CL') : '—'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setStatusDialogOpen(true)}
            sx={{ borderColor: IMG_PRIMARY, color: IMG_PRIMARY, fontWeight: 600 }}>
            Cambiar Estado
          </Button>
          <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={async () => { await printOrderPDF(order, activeBranch); }}
            sx={{ borderColor: '#64748B', color: '#64748B', fontWeight: 600 }}>
            Imprimir Orden
          </Button>
          {order.status === 'approved' || order.status === 'reported' ? (
            <Button variant="contained" startIcon={<PictureAsPdfIcon />}
              onClick={() => navigate(`/imaging/report/${order.id}`)}
              sx={{ bgcolor: IMG_TEAL, '&:hover': { bgcolor: '#229984' }, fontWeight: 600 }}>
              Ver Informe
            </Button>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Patient info */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: IMG_PRIMARY, width: 56, height: 56 }}>
                <PetsIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} color={IMG_PRIMARY}>{order.patientName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {order.patientSpecies} · {order.patientBreed} · {order.patientWeight ? `${order.patientWeight} kg` : ''}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => navigate(`/patients/${order.patientId}/record?returnTo=/imaging/orders/${order.id}`)}
                sx={{ mb: 1, borderRadius: 2, fontSize: '0.75rem' }}>
                Ver ficha del paciente
              </Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Propietario:</strong> {order.ownerName || '—'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BrokenImageIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">
                  <strong>Médico solicitante:</strong> {order.vetName || '—'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Studies requested */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY} mb={2}>
              Estudios Solicitados
            </Typography>
            <Grid container spacing={2}>
              {order.studiesRequested?.map((study, idx) => (
                <Grid key={idx} size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label={study.modality} size="small" sx={{ fontSize: '0.65rem', bgcolor: MODALITY_CONFIG[study.modality]?.bg, color: MODALITY_CONFIG[study.modality]?.color }} />
                      <Typography variant="body2" fontWeight={600}>{study.examTypeName}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Región: {study.bodyRegion || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Vistas: {study.views?.join(', ') || '—'}
                    </Typography>
                    {study.sedationRequired && (
                      <Chip label="Requiere sedación" size="small" sx={{ mt: 1, fontSize: '0.6rem', bgcolor: '#FEF3C7', color: '#92400E' }} />
                    )}
                    {study.contrastRequired && (
                      <Chip label={`Contraste: ${study.contrastType}`} size="small" sx={{ mt: 1, fontSize: '0.6rem', bgcolor: '#DBEAFE', color: '#1E40AF' }} />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Clinical indication */}
        <Grid size={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFBEB' }}>
            <Typography variant="subtitle1" fontWeight={700} color="#92400E" mb={1}>
              Indicación Clínica
            </Typography>
            <Typography variant="body2">{order.clinicalIndication || '—'}</Typography>
          </Paper>
        </Grid>

        {/* Image upload and gallery */}
        <Grid size={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY} mb={2}>
              Imágenes del Estudio
            </Typography>

            {/* Upload zone */}
            {(order.status === 'pending_imaging' || order.status === 'imaging_done') && (
              <Box
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  handleFileUpload(Array.from(e.dataTransfer.files));
                }}
                sx={{
                  border: `2px dashed ${dragOver ? IMG_TEAL : '#E2E8F0'}`,
                  borderRadius: 3, p: 4, textAlign: 'center',
                  bgcolor: dragOver ? '#F0FDF4' : '#FAFAFA',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  mb: 3,
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFileUpload(Array.from(e.target.files))}
                />
                <PhotoCameraIcon sx={{ fontSize: 40, color: dragOver ? IMG_TEAL : '#CBD5E1', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Arrastra las imágenes aquí o haz clic para seleccionar
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  JPEG, PNG, PDF · Máx. 20 MB por archivo
                </Typography>
              </Box>
            )}

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">{uploadProgress}%</Typography>
              </Box>
            )}

            {/* Image gallery */}
            {order.files && order.files.length > 0 ? (
              <Grid container spacing={2}>
                {order.files.map((file) => (
                  <Grid key={file.id} size={{ xs: 6, sm: 4, md: 3 }}>
                    <Card>
                      {file.fileType?.startsWith('image/') ? (
                        <CardMedia
                          component="img"
                          height="140"
                          image={file.downloadURL}
                          alt={file.fileName}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setSelectedImage(file)}
                        />
                      ) : (
                        <Box sx={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC' }}>
                          <PictureAsPdfIcon sx={{ fontSize: 48, color: '#DC2626' }} />
                        </Box>
                      )}
                      <CardActions sx={{ justifyContent: 'space-between', p: 1 }}>
                        <Typography variant="caption" noWrap sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.fileName}
                        </Typography>
                        <IconButton size="small" onClick={() => handleDeleteImage(file.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No hay imágenes subidas
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Status history */}
        <Grid size={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E2E8F0' }}>
            <Typography variant="subtitle1" fontWeight={700} color={IMG_PRIMARY} mb={2}>
              Historial de Estados
            </Typography>
            {order.statusHistory && order.statusHistory.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {order.statusHistory.map((entry, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: IMG_PRIMARY, mt: 4 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {IMAGING_STATUS_CONFIG[entry.status]?.label || entry.status}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entry.changedAt?.toDate ? entry.changedAt.toDate().toLocaleString('es-CL') : '—'} · {entry.changedByName || '—'}
                      </Typography>
                      {entry.notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {entry.notes}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Sin historial</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Image preview dialog */}
      <Dialog open={!!selectedImage} onClose={() => setSelectedImage(null)} maxWidth="md">
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">{selectedImage?.fileName}</Typography>
          <IconButton onClick={() => setSelectedImage(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage?.fileType?.startsWith('image/') ? (
            <img src={selectedImage.downloadURL} alt={selectedImage.fileName} style={{ maxWidth: '100%', height: 'auto' }} />
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PictureAsPdfIcon sx={{ fontSize: 64, color: '#DC2626' }} />
              <Typography variant="body2" sx={{ mt: 2 }}>Archivo PDF</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Status change dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="sm">
        <DialogTitle>Cambiar Estado</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Nuevo estado"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            sx={{ mt: 2 }}
            InputLabelProps={{ shrink: true }}
          >
            {Object.entries(IMAGING_STATUS_CONFIG).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleStatusChange} variant="contained">Guardar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
